import React, { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n";

const COLORS = [
    { name: "black", hex: "#2c352e" },
    { name: "red", hex: "#b0453c" },
    { name: "rose", hex: "#c08a7e" },
    { name: "green", hex: "#4a5d4e" },
    { name: "blue", hex: "#2d4a6f" },
    { name: "gold", hex: "#c5a880" },
];

const SIZES = [
    { key: "thin", px: 2 },
    { key: "medium", px: 4 },
    { key: "thick", px: 7 },
    { key: "bold", px: 11 },
];

export default function DrawingCanvas({ onSend, disabled, requireName = false }) {
    const { t, dir } = useLang();
    const canvasRef = useRef(null);
    const [color, setColor] = useState(COLORS[0]);
    const [size, setSize] = useState(SIZES[1]);
    const [senderName, setSenderName] = useState("");
    const [history, setHistory] = useState([]);
    const drawing = useRef(false);
    const lastPoint = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.fillStyle = "#fdfbf7";
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        saveHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const saveHistory = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const data = canvas.toDataURL("image/png");
        setHistory((h) => [...h.slice(-15), data]);
    };

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        lastPoint.current = getPos(e);
    };

    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const p = getPos(e);
        ctx.strokeStyle = color.hex;
        ctx.lineWidth = size.px;
        ctx.beginPath();
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastPoint.current = p;
    };

    const end = () => {
        if (drawing.current) {
            drawing.current = false;
            saveHistory();
        }
    };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const rect = canvas.getBoundingClientRect();
        ctx.fillStyle = "#fdfbf7";
        ctx.fillRect(0, 0, rect.width, rect.height);
        saveHistory();
    };

    const undo = () => {
        setHistory((h) => {
            if (h.length <= 1) return h;
            const next = h.slice(0, -1);
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext("2d");
                const rect = canvas.getBoundingClientRect();
                ctx.fillStyle = "#fdfbf7";
                ctx.fillRect(0, 0, rect.width, rect.height);
                ctx.drawImage(img, 0, 0, rect.width, rect.height);
            };
            img.src = next[next.length - 1];
            return next;
        });
    };

    const handleSend = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (requireName && !senderName.trim()) return;
        const data = canvas.toDataURL("image/png");
        onSend({ type: "drawing", drawing_data_url: data, sender_name: senderName });
    };

    return (
        <div className="space-y-4" dir={dir}>
            {/* Color palette */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="wd-label text-[var(--wd-text-muted)]">{t("colors")}:</span>
                    {COLORS.map((c) => (
                        <button
                            key={c.name}
                            type="button"
                            onClick={() => setColor(c)}
                            data-testid={`color-${c.name}`}
                            className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                                color.name === c.name ? "border-[var(--wd-primary)] scale-110" : "border-white"
                            } shadow-sm`}
                            style={{ background: c.hex }}
                            aria-label={c.name}
                        />
                    ))}
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <span className="wd-label text-[var(--wd-text-muted)]">{t("size")}:</span>
                {SIZES.map((s) => (
                    <button
                        key={s.key}
                        type="button"
                        onClick={() => setSize(s)}
                        data-testid={`brush-${s.key}`}
                        className={`px-3 py-1 rounded-full text-xs border transition-all ${
                            size.key === s.key
                                ? "border-[var(--wd-primary)] bg-[var(--wd-primary)] text-white"
                                : "border-[var(--wd-border)] text-[var(--wd-text-muted)] hover:border-[var(--wd-primary)]"
                        }`}
                    >
                        {t(s.key)}
                    </button>
                ))}
            </div>

            <p className="text-xs text-[var(--wd-text-muted)]">
                {t("current")}: <span style={{ color: color.hex }} className="font-medium">{color.name}</span>
                <span className="mx-2">·</span>
                {t("size")}: {t(size.key)}
            </p>

            <div className="relative rounded-xl overflow-hidden border border-[var(--wd-border)] bg-[#fdfbf7]" style={{ touchAction: "none" }}>
                <canvas
                    ref={canvasRef}
                    data-testid="drawing-canvas"
                    className="block w-full h-64 md:h-80 cursor-crosshair"
                    onMouseDown={start}
                    onMouseMove={move}
                    onMouseUp={end}
                    onMouseLeave={end}
                    onTouchStart={start}
                    onTouchMove={move}
                    onTouchEnd={end}
                />
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <button type="button" onClick={undo} className="wd-btn-ghost wd-btn" data-testid="drawing-undo-btn">
                    {t("undo")}
                </button>
                <button type="button" onClick={clear} className="wd-btn-ghost wd-btn" data-testid="drawing-clear-btn">
                    {t("clear")}
                </button>
            </div>

            <input
                type="text"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder={t("yourName")}
                className="wd-input"
                data-testid="drawing-sender-name"
            />

            <div className="pt-2">
                <button
                    type="button"
                    onClick={handleSend}
                    disabled={disabled}
                    className="wd-btn w-full"
                    data-testid="drawing-send-btn"
                >
                    {t("sendMessage")}
                </button>
            </div>
        </div>
    );
}
